export interface JWTPayload {
  email: string;
  exp: number;
  login_user_id: string;
  user_id: string;
  username: string;
}

/**
 * Custom error class for JWT-related errors
 */
export class JWTError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'JWTError';
  }
}

/**
 * Extracts and decodes the JWT token from cookies
 * @returns Decoded JWT payload or null if no valid token is found
 * @throws {JWTError} If token is malformed or cookie is corrupted
 */
export const getJWTInfoFromCookie = (): JWTPayload | null => {
  const cookie = document.cookie.split(';').find((c) => c.trim().toLowerCase().startsWith('jwt='));
  if (!cookie) return null;

  const [, ...restParts] = cookie.split('=');
  const token = restParts.join('=').trim(); // Handle cases where value contains '='
  if (!token) throw new JWTError('JWT cookie found but value is empty');

  return parseJwt(token);
};

/**
 * Parses a JWT token and returns its payload
 * @param token - The JWT token string
 * @returns Decoded JWT payload or null if token is invalid
 * @throws {JWTError} If token is malformed
 */
export const parseJwt = (token: string): JWTPayload | null => {
  try {
    if (!token) return null;

    const parts = token.split('.');
    if (parts.length !== 3) throw new JWTError('Invalid JWT format');

    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const padding = '='.repeat((4 - (base64.length % 4)) % 4);
    const paddedBase64 = base64 + padding;
    const decoded = window.atob(paddedBase64);
    const bytes = new Uint8Array(decoded.length);
    for (let i = 0; i < decoded.length; i++) bytes[i] = decoded.charCodeAt(i);

    const jsonPayload = new TextDecoder().decode(bytes);
    const payload = JSON.parse(jsonPayload) as JWTPayload;
    if (typeof payload !== 'object' || payload === null) throw new JWTError('Invalid JWT payload');

    return payload;
  } catch (e) {
    if (e instanceof JWTError) throw e;

    return null;
  }
};
